<template>
    <Head v-if="updating === false" title="Create New Organisation" />
    <Head v-else :title="organisation.name" />
    <ValidationErrors :show="showErrors" />
    <Audit v-if="openModal" @close="openModal = false" :open="openModal" :activity_logs="organisation.activity_logs" />
    <div class="w-full space-y-3 mb-6">
        <div>
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                <span v-if="form.organisation.name === null" v-html="'Create New ' + organisation.type.charAt(0).toUpperCase() + organisation.type.substring(1)"></span>
                <span v-else v-html="form.organisation.name"></span>
            </h1>
            <h2 class="mb-2 mt-0 text-md font-medium leading-7 text-gray-500 sm:truncate">
                <span v-if="form.organisation.registered_legal_name === null" v-html="''"></span>
                <span v-else v-html="form.organisation.registered_legal_name"></span>
            </h2>
        </div>
        <div class="space-y-6 mt-2 lg:col-span-9">
            <section aria-labelledby="payment-details-heading">
                <form @submit.prevent="submit" disabled>
                    <fieldset :class="{'cursor-not-allowed': !userCheck}" :disabled="!userCheck">
                        <div v-if="updating === true" class="py-3 flex justify-between">
                            <div class="py-3 flex justify-start">
                                <Link v-if="updating"
                                    :href="route('frontend.organisations.suppliers.index', { _query: { organisation_id: organisation.id, open_contacts_slideout: true } })"
                                    class="inline-flex h-10 items-center px-4 py-2 bg-gray-800 border border-transparent rounded-md font-semibold text-xs text-white uppercase bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                                    <users-icon class="-ml-0.5 mr-2 h-4 w-4"></users-icon>
                                    Manage Contacts
                                </Link>
                                <button type="button" @click="openModal = true"
                                    class="bg-amaze-coral border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-700">
                                    Show Changelog
                                </button>
                                <Link v-if="organisation.deleted_at == null"
                                    :href="''"
                                    @click="archive"
                                    :class="{'cursor-not-allowed': !userCheck}"
                                    :disabled="!userCheck"
                                    class="inline-flex h-10 items-center px-4 py-2 ml-5 bg-red-500 border border-transparent rounded-md font-semibold text-xs text-white uppercase focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                                    <archive-icon class="-ml-0.5 mr-2 h-4 w-4"></archive-icon>
                                    Archive {{ organisation.type }}
                                </Link>
                                <Link v-else
                                    :href="''"
                                    @click="unarchive"
                                    :class="{'cursor-not-allowed': !userCheck}"
                                    :disabled="!userCheck"
                                    class="inline-flex h-10 items-center px-4 py-2 ml-5 bg-green-500 border border-transparent rounded-md font-semibold text-xs text-white uppercase focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                                    <archive-icon class="-ml-0.5 mr-2 h-4 w-4"></archive-icon>
                                    Unarchive {{ organisation.type }}
                                </Link>
                            </div>
                            <div class="py-3 flex justify-end">
                                <a href="#" onclick="history.back();return false;"
                                    class="inline-flex h-10 items-center px-4 py-2 bg-gray-800 border border-transparent rounded-md text-sm text-white bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                                    Back
                                </a>
                                <button type="submit"
                                    :class="{'cursor-not-allowed': !userCheck}"
                                    :disabled="!userCheck"
                                    class="ml-5 bg-amaze-coral border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-700">
                                    <span v-if="updating === true">Update</span>
                                    <span v-else>Save</span>
                                </button>
                            </div>
                        </div>
                        <div class="shadow sm:rounded-md sm:overflow-hidden">
                            <div class="bg-white px-4 p-6 space-y-6">
                                <!-- Organisation Form Start -->
                                <div id="organisation_form" class="space-y-6">
                                    <div class="border-b border-gray-200 pb-2">
                                        <div class="relative">
                                            <div class="absolute inset-0 flex items-center" aria-hidden="true">
                                                <div class="w-full border-t border-gray-300" />
                                            </div>
                                            <div class="relative flex items-center justify-between">
                                                <span class="pr-3 bg-white text-lg font-bold text-gray-900"> Organisation
                                                    Information
                                                </span>
                                            </div>
                                        </div>
                                        <div class="flex justify-between items-center">
                                            <p class="mt-1 text-sm text-gray-500 w-1/2">Enter information for the
                                                organisation
                                                below.</p>

                                            <p class="mt-1 w-full text-right text-xs text-gray-500 font-italic w-1/2">Fields
                                                marked
                                                with an asterisk
                                                (<span class="text-red-500">*</span>) are
                                                required.</p>
                                        </div>
                                    </div>

                                    <div class="grid grid-cols-4 gap-6">
                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="name"
                                                class="block text-sm font-medium text-gray-700 required">Name</label>
                                            <input type="text" v-model="form.organisation.name" name="name" id="name"
                                                autocomplete="name"
                                                :class="{ 'border-red-500': form.errors['organisation.name'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="registered_legal_name"
                                                class="block text-sm font-medium text-gray-700 required">Registered
                                                Legal Name</label>
                                            <input type="text" v-model="form.organisation.registered_legal_name"
                                                name="registered_legal_name" id="registered_legal_name"
                                                autocomplete="registered_legal_name"
                                                :class="{ 'border-red-500': form.errors['organisation.registered_legal_name'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="cmid" class="block text-sm font-medium text-gray-700">CMID</label>
                                            <input type="text" v-model="form.organisation.cmid" name="cmid" id="cmid"
                                                placeholder="CM00000"
                                                :class="{ 'border-red-500': form.errors['organisation.cmid'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="smid" class="block text-sm font-medium text-gray-700">SMID</label>
                                            <input type="text" v-model="form.organisation.smid" name="smid" id="smid"
                                                placeholder="SM00000"
                                                :class="{ 'border-red-500': form.errors['organisation.smid'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-8 sm:col-span-4"></div>


                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1 hidden">
                                            <label for="organisation_type"
                                                class="block text-sm font-medium text-gray-700 required">Organisation
                                                Type</label>
                                            <select id="organisation_type" v-model="form.organisation.type"
                                                :class="{ 'border-red-500': form.errors['organisation.type'] }"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                <option value selected v-html="'Select an organisation type'"></option>
                                                <option v-for="type in organisation_types" :value="type"
                                                    v-html="type.charAt(0).toUpperCase() + type.substring(1)"></option>
                                            </select>
                                        </div>


                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="telephone"
                                                class="block text-sm font-medium text-gray-700">Telephone</label>
                                            <input v-model="form.organisation.telephone" type="text" name="telephone"
                                                id="telephone" autocomplete="telephone"
                                                :class="{ 'border-red-500': form.errors['organisation.telephone'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="mobile"
                                                class="block text-sm font-medium text-gray-700">Mobile</label>
                                            <input v-model="form.organisation.mobile" type="text" name="mobile" id="mobile"
                                                autocomplete="mobile"
                                                :class="{ 'border-red-500': form.errors['organisation.mobile'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
                                            <input v-model="form.organisation.email" type="text" name="email" id="email"
                                                autocomplete="email"
                                                :class="{ 'border-red-500': form.errors['organisation.email'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="website"
                                                class="block text-sm font-medium text-gray-700">Website</label>
                                            <input v-model="form.organisation.website" type="text" name="website"
                                                id="website" autocomplete="website"
                                                :class="{ 'border-red-500': form.errors['organisation.website'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                    </div>

                                    <div>
                                        <h2 id="organisation-address-heading"
                                            class="text-lg leading-6 font-medium text-gray-900">Address</h2>
                                        <p class="mt-1 text-sm text-gray-500">Enter the address for the organisation below.
                                            The
                                            address entered here is used for reference.</p>
                                    </div>

                                    <div class="grid grid-cols-4 gap-6">
                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="street"
                                                class="block text-sm font-medium text-gray-700">Street</label>
                                            <input v-model="form.organisation_address.street" type="text" name="street"
                                                id="street" autocomplete="street"
                                                :class="{ 'border-red-500': form.errors['organisation_address.street'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="address_line_1"
                                                class="block text-sm font-medium text-gray-700">Address
                                                Line 1</label>
                                            <input v-model="form.organisation_address.address_line_1" type="text"
                                                name="address_line_1" id="address_line_1" autocomplete="address_line_1"
                                                :class="{ 'border-red-500': form.errors['organisation_address.address_line_1'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="address_line_2"
                                                class="block text-sm font-medium text-gray-700">Address
                                                Line 2</label>
                                            <input v-model="form.organisation_address.address_line_2" type="text"
                                                name="address_line_2" id="address_line_2" autocomplete="address_line_2"
                                                :class="{ 'border-red-500': form.errors['organisation_address.address_line_2'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="address_line_3"
                                                class="block text-sm font-medium text-gray-700">Address
                                                Line 3</label>
                                            <input v-model="form.organisation_address.address_line_3" type="text"
                                                name="address_line_3" id="address_line_3" autocomplete="address_line_3"
                                                :class="{ 'border-red-500': form.errors['organisation_address.address_line_3'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="city" class="block text-sm font-medium text-gray-700">City</label>
                                            <input v-model="form.organisation_address.city" type="text" name="city"
                                                id="city" autocomplete="city"
                                                :class="{ 'border-red-500': form.errors['organisation_address.city'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="code" class="block text-sm font-medium text-gray-700">Postal / Zip
                                                Code</label>
                                            <input v-model="form.organisation_address.code" type="text" name="code"
                                                id="code" autocomplete="code"
                                                :class="{ 'border-red-500': form.errors['organisation_address.code'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="email"
                                                class="block text-sm font-medium text-gray-700 required">Country</label>
                                            <select v-model="form.organisation_address.country_id" id="country"
                                                :class="{ 'border-red-500': form.errors['organisation_address.country_id'] }"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                <option value>Select a country</option>
                                                <option v-for="(country, index) in countries" :value="country.id"
                                                    v-html="country.name"></option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <!-- Organisation Form End -->
                                <!-- Supplier Form Start -->
                                <div id="supplier_form" class="space-y-6" v-if="form.organisation.type === 'supplier'">
                                    <div class="relative">
                                        <div class="absolute inset-0 flex items-center" aria-hidden="true">
                                            <div class="w-full border-t border-gray-300" />
                                        </div>
                                        <div class="relative flex items-center justify-between">
                                            <span class="pr-3 bg-white text-lg font-bold text-gray-900"> Service
                                                Information </span>
                                            <button @click="copyToService" type="button"
                                                v-if="form.organisation.type === 'supplier'"
                                                class="inline-flex items-center shadow-sm px-4 py-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                <ClipboardCopyIcon class="-ml-1.5 mr-1 h-5 w-5 text-gray-400"
                                                    aria-hidden="true" />
                                                <span>Copy Organisation Information</span>
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <p class="-mt-6 text-sm text-gray-500">Enter the service information for the
                                            supplier
                                            below. The information entered here will be used for creating services.</p>
                                        <div class="rounded-md bg-blue-50 p-4 mt-2">
                                            <div class="flex">
                                                <div class="flex-shrink-0">
                                                    <InformationCircleIcon class="h-5 w-5 text-blue-400"
                                                        aria-hidden="true" />
                                                </div>
                                                <div class="ml-3 flex-1 md:flex md:justify-between">
                                                    <p class="text-sm text-blue-700">The service type, country and region
                                                        fields are used to filter services when adding them to a quote
                                                        within a project.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="grid grid-cols-4 gap-6">

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service_type"
                                                class="block text-sm font-medium text-gray-700 required">Service
                                                Type</label>
                                            <select id="service_type" v-model="form.service.service_type_id"
                                                name="service_type"
                                                :class="{ 'border-red-500': form.errors['service.service_type_id'] }"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                <option value selected>Select a service type</option>
                                                <option v-for="service_type in service_types" :value="service_type.id"
                                                    v-html="service_type.name"></option>
                                            </select>
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service-name"
                                                class="block text-sm font-medium text-gray-700 required">Name</label>
                                            <input type="text" v-model="form.service.name" name="service-name"
                                                id="service-name" autocomplete="name"
                                                :class="{ 'border-red-500': form.errors['service.name'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1"></div>
                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1"></div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service-telephone"
                                                class="block text-sm font-medium text-gray-700">Telephone</label>
                                            <input v-model="form.service.telephone" type="text" name="service-telephone"
                                                id="service-telephone" autocomplete="telephone"
                                                :class="{ 'border-red-500': form.errors['service.telephone'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service-mobile"
                                                class="block text-sm font-medium text-gray-700">Mobile</label>
                                            <input v-model="form.service.mobile" type="text" name="service-mobile"
                                                id="service-mobile" autocomplete="mobile"
                                                :class="{ 'border-red-500': form.errors['service.mobile'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service-email"
                                                class="block text-sm font-medium text-gray-700">Email</label>
                                            <input v-model="form.service.email" type="text" name="service-email"
                                                id="service-email" autocomplete="email"
                                                :class="{ 'border-red-500': form.errors['service.email'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service-website"
                                                class="block text-sm font-medium text-gray-700">Website</label>
                                            <input v-model="form.service.website" type="text" name="service-website"
                                                id="service-website" autocomplete="website"
                                                :class="{ 'border-red-500': form.errors['organisation.website'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                    </div>

                                    <div>
                                        <h2 id="service-address-heading"
                                            class="text-lg leading-6 font-medium text-gray-900">Address</h2>
                                        <p class="mt-1 text-sm text-gray-500">Enter the service address for the
                                            service. The information entered here will be used for reference only.</p>
                                    </div>

                                    <div class="pt-6 grid grid-cols-4 gap-6" hidden>
                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service_street"
                                                class="block text-sm font-medium text-gray-700">Street</label>
                                            <input v-model="form.service_address.street" type="text"
                                                name="service-address-street" id="service-address-street"
                                                autocomplete="service_street"
                                                :class="{ 'border-red-500': form.errors['service_address.street'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />

                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service_address_line_1"
                                                class="block text-sm font-medium text-gray-700">Address
                                                Line 1</label>
                                            <input v-model="form.service_address.address_line_1" type="text"
                                                name="service-address-address-line-1" id="service-address-address-line-1"
                                                autocomplete="address_line_1"
                                                :class="{ 'border-red-500': form.errors['service_address.address_line_1'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service_address_line_2"
                                                class="block text-sm font-medium text-gray-700">Address
                                                Line 2</label>
                                            <input v-model="form.service_address.address_line_2" type="text"
                                                name="service-address-address-line-2" id="service-address-address-line-2"
                                                autocomplete="address_line_2"
                                                :class="{ 'border-red-500': form.errors['service_address.address_line_2'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service_address_line_3"
                                                class="block text-sm font-medium text-gray-700">Address
                                                Line 3</label>
                                            <input v-model="form.service_address.address_line_3" type="text"
                                                name="service-address-address-line-3" id="service-address-address-line-3"
                                                autocomplete="address_line_3"
                                                :class="{ 'border-red-500': form.errors['service_address.address_line_3'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service_city"
                                                class="block text-sm font-medium text-gray-700">City</label>
                                            <input v-model="form.service_address.city" type="text"
                                                name="service-address-city" id="service-address-city" autocomplete="city"
                                                :class="{ 'border-red-500': form.errors['service_address.city'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service_code" class="block text-sm font-medium text-gray-700">Postal
                                                /
                                                Zip Code</label>
                                            <input v-model="form.service_address.code" type="text"
                                                name="service-address-code" id="service-address-code" autocomplete="code"
                                                :class="{ 'border-red-500': form.errors['service_address.code'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service-address-country"
                                                class="block text-sm font-medium text-gray-700 required">Country</label>
                                            <select v-model="form.service_address.country_id" id="service-address-country"
                                                :class="{ 'border-red-500': form.errors['service_address.country_id'] }"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                <option value="">Select a country</option>
                                                <option v-for="(country, index) in countries" :value="country.id"
                                                    :data-index="index" v-html="country.name"></option>
                                            </select>
                                        </div>

                                        <div class="col-span-4 mb-[70px] clear-both">
                                            <label for="service-notes"
                                                class="block text-sm font-medium text-gray-700">Notes</label>
                                            <quill-editor content-type="html" v-model:content="form.service.notes"
                                                :class="{ 'border-red-500': form.errors['service.notes'] }"
                                                class="min-h-[150px] mt-1 block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                                theme="snow"></quill-editor>
                                        </div>
                                    </div>
                                </div>
                                <!-- Supplier Form End -->
                                <!-- Linked Projects -->
                                <div id="project_form" class="space-y-6" v-if="this.organisation.projects && this.organisation.projects.length > 0">
                                    <div class="relative">
                                        <div class="absolute inset-0 flex items-center" aria-hidden="true">
                                            <div class="w-full border-t border-gray-300" />
                                        </div>
                                        <div class="relative flex items-center justify-between">
                                            <span class="pr-3 bg-white text-lg font-bold text-gray-900">
                                                Linked Projects
                                            </span>
                                        </div>
                                    </div>
                                    <div>
                                        <p class="-mt-6 text-sm text-gray-500">
                                            A list of projects that this organisation is linked to.
                                        </p>
                                    </div>
                                    <div class="flex flex-col mt-3 space-y-3">
                                        <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                            <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                                <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                                    <table class="min-w-full divide-y divide-gray-200">
                                                        <thead class="bg-gray-50">
                                                            <tr>
                                                                <th scope="col"
                                                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                    Project
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr v-for="(project, index) in this.organisation.projects"
                                                                :key="index"
                                                                :class="{'bg-gray-100': index % 2}" class="hover:bg-gray-50 cursor-pointer">
                                                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                    <a :href="route('frontend.project.show' , project.id)">
                                                                        {{ project.title }}
                                                                    </a>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <!-- Linked Services -->
                                <div id="project_form" class="space-y-6" v-if="this.organisation.quote_services && this.organisation.quote_services.length > 0">
                                    <div class="relative">
                                        <div class="absolute inset-0 flex items-center" aria-hidden="true">
                                            <div class="w-full border-t border-gray-300" />
                                        </div>
                                        <div class="relative flex items-center justify-between">
                                            <span class="pr-3 bg-white text-lg font-bold text-gray-900">
                                                Linked Offers
                                            </span>
                                        </div>
                                    </div>
                                    <div>
                                        <p class="-mt-6 text-sm text-gray-500">
                                            A list of offers that this organisation is linked to.
                                        </p>
                                    </div>
                                    <div class="flex flex-col mt-3 space-y-3">
                                        <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                            <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                                <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                                    <table class="min-w-full divide-y divide-gray-200">
                                                        <thead class="bg-gray-50">
                                                            <tr>
                                                                <th scope="col"
                                                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                    Service
                                                                </th>
                                                                <th scope="col"
                                                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                    Offer
                                                                </th>
                                                                <th scope="col"
                                                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                    Project
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr v-for="(service, index) in this.organisation.quote_services"
                                                                :key="index"
                                                                :class="{'bg-gray-100': index % 2}" class="hover:bg-gray-50 cursor-pointer">
                                                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                    <a v-if="service.quote.status === 'Confirmed'" :href="route('frontend.project.quote.show.accepted', [service.quote.project.id, service.quote.id])">
                                                                        {{ service.name }}
                                                                    </a>
                                                                    <a v-else :href="route('frontend.project.quote.show', [service.quote.project.id, service.quote.id])">
                                                                        {{ service.name }}
                                                                    </a>
                                                                </td>
                                                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                    <a v-if="service.quote.status === 'Confirmed'" :href="route('frontend.project.quote.show.accepted', [service.quote.project.id, service.quote.id])">
                                                                        {{ service.quote.name }}
                                                                    </a>
                                                                    <a v-else :href="route('frontend.project.quote.show', [service.quote.project.id, service.quote.id])">
                                                                        {{ service.quote.name }}
                                                                    </a>
                                                                </td>
                                                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                    <a v-if="service.quote.status === 'Confirmed'" :href="route('frontend.project.quote.show.accepted', [service.quote.project.id, service.quote.id])">
                                                                        {{ service.quote.project.title }}
                                                                    </a>
                                                                    <a v-else :href="route('frontend.project.quote.show', [service.quote.project.id, service.quote.id])">
                                                                        {{ service.quote.project.title }}
                                                                    </a>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <!-- Tax Information Form Start -->
                                <div id="tax_form" class="space-y-6">
                                    <div class="relative">
                                        <div class="absolute inset-0 flex items-center" aria-hidden="true">
                                            <div class="w-full border-t border-gray-300" />
                                        </div>
                                        <div class="relative flex items-center justify-between">
                                            <span class="pr-3 bg-white text-lg font-bold text-gray-900"> Tax & Billing
                                                Details </span>
                                            <div class="flex items-center gap-2">
                                                <button @click="copyToTax" type="button"
                                                    class="inline-flex items-center shadow-sm px-4 py-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                    <ClipboardCopyIcon class="-ml-1.5 mr-1 h-5 w-5 text-gray-400"
                                                        aria-hidden="true" />
                                                    <span>Copy Organisation Information</span>
                                                </button>

                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <p class="-mt-6 text-sm text-gray-500">Enter the tax and billing details for this
                                            organisation.</p>
                                        <div class="rounded-md bg-blue-50 p-4 mt-2">
                                            <div class="flex">
                                                <div class="flex-shrink-0">
                                                    <InformationCircleIcon class="h-5 w-5 text-blue-400"
                                                        aria-hidden="true" />
                                                </div>
                                                <div class="ml-3 flex-1 md:flex md:justify-between">
                                                    <p class="text-sm text-blue-700">Details entered here will appear on
                                                        organisation invoices.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="grid grid-cols-4 gap-6">

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="tax-company-name"
                                                class="block text-sm font-medium text-gray-700">Company
                                                Name</label>
                                            <input type="text" v-model="form.tax.company_name" name="tax-company-name"
                                                id="tax-company-name" autocomplete="name"
                                                :class="{ 'border-red-500': form.errors['tax.company_name'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />

                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="tax-vat-id" class="block text-sm font-medium text-gray-700">VAT
                                                ID</label>
                                            <input type="text" v-model="form.tax.vat_id" name="tax-vat-id" id="tax-vat-id"
                                                autocomplete="name" :class="{ 'border-red-500': form.errors['tax.vat_id'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                    </div>

                                    <div>
                                        <h2 id="tax-address-heading" class="text-lg leading-6 font-medium text-gray-900">
                                            Address</h2>
                                    </div>

                                    <div class="pt-6 grid grid-cols-4 gap-6" hidden>
                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="tax-address-street"
                                                class="block text-sm font-medium text-gray-700">Street</label>
                                            <input v-model="form.tax_address.street" type="text" name="tax-address-street"
                                                id="tax-address-street" autocomplete="tax-address-street"
                                                :class="{ 'border-red-500': form.errors['tax_address.street'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />

                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="tax-address-address-line-1"
                                                class="block text-sm font-medium text-gray-700">Address
                                                Line 1</label>
                                            <input v-model="form.tax_address.address_line_1" type="text"
                                                name="tax-address-address-line-1" id="tax-address-address-line-1"
                                                autocomplete="address-line-1"
                                                :class="{ 'border-red-500': form.errors['tax_address.address_line_1'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="tax-address-address-line-2"
                                                class="block text-sm font-medium text-gray-700">Address
                                                Line 2</label>
                                            <input v-model="form.tax_address.address_line_2" type="text"
                                                name="tax-address-address-line-2" id="tax-address-address-line-2"
                                                autocomplete="address-line-2"
                                                :class="{ 'border-red-500': form.errors['tax_address.address_line_2'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="tax-address-address-line-3"
                                                class="block text-sm font-medium text-gray-700">Address
                                                Line 3</label>
                                            <input v-model="form.tax_address.address_line_3" type="text"
                                                name="tax-address-address-line-3" id="tax-address-address-line-3"
                                                autocomplete="address-line-3"
                                                :class="{ 'border-red-500': form.errors['tax_address.address_line_3'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="tax-address-city"
                                                class="block text-sm font-medium text-gray-700">City</label>
                                            <input v-model="form.tax_address.city" type="text" name="tax-address-city"
                                                id="tax-address-city" autocomplete="city"
                                                :class="{ 'border-red-500': form.errors['tax_address.city'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="tax-address-code"
                                                class="block text-sm font-medium text-gray-700">Postal
                                                /
                                                Zip Code</label>
                                            <input v-model="form.tax_address.code" type="text" name="tax-address-code"
                                                id="tax-address-code" autocomplete="code"
                                                :class="{ 'border-red-500': form.errors['tax_address.code'] }"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="tax-address-country"
                                                class="block text-sm font-medium text-gray-700">Country</label>
                                            <select v-model="form.tax_address.country_id" id="tax-address-country"
                                                name="tax-address-country"
                                                :class="{ 'border-red-500': form.errors['tax_address.country_id'] }"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                <option value="">Select a country</option>
                                                <option v-for="country in countries" :value="country.id"
                                                    v-html="country.name"></option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <!-- Tax Information Form End -->

                            </div>
                            <div class="px-4 py-3 bg-gray-50 text-right sm:px-6">
                                <button type="submit"
                                    :class="{'cursor-not-allowed': !userCheck}"
                                    :disabled="!userCheck"
                                    class="bg-amaze-coral border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-700">
                                    <span v-if="updating === true">Update</span>
                                    <span v-else>Save</span>
                                </button>
                            </div>
                        </div>
                    </fieldset>
                </form>
            </section>
        </div>
    </div>
</template>

<script>
import Authenticated from "@/Layouts/Authenticated.vue";
import { computed, ref, reactive } from "vue";
import debounce from "lodash/debounce";
import pickBy from "lodash/pickBy";
import { Link, Head, usePage, useForm } from "@inertiajs/vue3";
import { ClipboardCopyIcon, PlusSmIcon, UsersIcon, InformationCircleIcon, ArchiveIcon } from "@heroicons/vue/outline"
import ValidationErrors from "@/Components/ValidationErrors";
import Audit from "@/Components/Modal/Audit.vue";

export default {
    layout: Authenticated,
    components: {
        ValidationErrors,
        Link,
        Head,
        ClipboardCopyIcon,
        PlusSmIcon,
        UsersIcon,
        InformationCircleIcon,
        ArchiveIcon,
        Audit
    },
    setup(props) {
        const user = computed(() => usePage().props.auth.user);
        const roles = computed(() => usePage().props.auth.roles ?? []);

        const userCheck = computed(() => {
            const isSuperAdmin = roles.value.includes('super-admin') || roles.value.includes('Super Admin');

            return isSuperAdmin
                || user.value.name === 'Maria Diaz'
                || user.value.name === 'Andreas Scholten';
        });

        const organisation = usePage().props.organisation ?? null;
        const updating = usePage().props.updating;
        const openModal = ref(false);

        const form = useForm({
            organisation: {
                id: updating === true ? organisation.id : null,
                name: updating === true ? organisation.name : null,
                registered_legal_name: updating === true ? organisation.registered_legal_name : null,
                cmid: updating === true ? organisation.cmid : null,
                smid: updating === true ? organisation.smid : null,
                type: organisation.type,
                telephone: updating === true ? organisation.telephone : null,
                mobile: updating === true ? organisation.mobile : null,
                website: updating === true ? organisation.website : null,
                email: updating === true ? organisation.email : null,
                management_fee_separate: updating === true ? (organisation.management_fee_separate == 1 ? true : false) : false
            },
            organisation_address: {
                street: updating === true ? organisation.address.street : null,
                address_line_1: updating === true ? organisation.address.address_line_1 : null,
                address_line_2: updating === true ? organisation.address.address_line_2 : null,
                address_line_3: updating === true ? organisation.address.address_line_3 : null,
                city: updating === true ? organisation.address.city : null,
                code: updating === true ? organisation.address.code : null,
                country_id: updating === true ? organisation.address.country_id : "",
            },
            service: {
                name: (updating === true && ("service" in organisation)) ? organisation.service.name : null,
                service_type_id: (updating === true && ("service" in organisation)) ? organisation.service.service_type_id : "",
                telephone: (updating === true && ("service" in organisation)) ? organisation.service.telephone : null,
                mobile: (updating === true && ("service" in organisation)) ? organisation.service.mobile : null,
                email: (updating === true && ("service" in organisation)) ? organisation.service.email : null,
                website: (updating === true && ("service" in organisation)) ? organisation.service.website : null,
                notes: (updating === true && ("service" in organisation)) ? organisation.service.notes : null
            },
            service_address: {
                street: (updating === true && ("service" in organisation)) ? organisation.service.address.street : null,
                address_line_1: (updating === true && ("service" in organisation)) ? organisation.service.address.address_line_1 : null,
                address_line_2: (updating === true && ("service" in organisation)) ? organisation.service.address.address_line_2 : null,
                address_line_3: (updating === true && ("service" in organisation)) ? organisation.service.address.address_line_3 : null,
                city: (updating === true && ("service" in organisation)) ? organisation.service.address.city : null,
                code: (updating === true && ("service" in organisation)) ? organisation.service.address.code : null,
                country_id: (updating === true && ("service" in organisation)) ? organisation.service.address.country_id : ""
            },
            tax: {
                company_name: (updating === true && ("tax" in organisation) && organisation.tax != null) ? organisation.tax.company_name : null,
                vat_id: (updating === true && ("tax" in organisation)  && organisation.tax != null) ? organisation.tax.vat_id : null
            },
            tax_address: {
                street: (updating === true && ("tax" in organisation) && organisation.tax != null && organisation.tax.address != null) ? organisation.tax.address.street : null,
                address_line_1: (updating === true && ("tax" in organisation) && organisation.tax != null && organisation.tax.address != null) ? organisation.tax.address.address_line_1 : null,
                address_line_2: (updating === true && ("tax" in organisation) && organisation.tax != null && organisation.tax.address != null) ? organisation.tax.address.address_line_2 : null,
                address_line_3: (updating === true && ("tax" in organisation) && organisation.tax != null && organisation.tax.address != null) ? organisation.tax.address.address_line_3 : null,
                city: (updating === true && ("tax" in organisation) && organisation.tax != null && organisation.tax.address != null) ? organisation.tax.address.city : null,
                code: (updating === true && ("tax" in organisation) && organisation.tax != null && organisation.tax.address != null) ? organisation.tax.address.code : null,
                country_id: (updating === true && ("tax" in organisation) && organisation.tax != null && organisation.tax.address != null) ? organisation.tax.address.country_id : ""
            }
        });


        // Organisation Types
        const organisation_types = usePage().props.organisation_types;

        // Organisation Types
        const service_types = usePage().props.service_types;

        // Countries
        const countries = usePage().props.countries;

        // Currencies
        const currencies = usePage().props.currencies;

        // Show errors
        const showErrors = false;


        /**
         * Copy organisation information to the service.
         */
        function copyToService() {
            this.form.service.name = this.form.organisation.name;
            this.form.service.telephone = this.form.organisation.telephone;
            this.form.service.mobile = this.form.organisation.mobile;
            this.form.service.email = this.form.organisation.email;
            this.form.service.website = this.form.organisation.website;
            this.form.service_address.street = this.form.organisation_address.street;
            this.form.service_address.address_line_1 = this.form.organisation_address.address_line_1;
            this.form.service_address.address_line_2 = this.form.organisation_address.address_line_2;
            this.form.service_address.address_line_3 = this.form.organisation_address.address_line_3;
            this.form.service_address.code = this.form.organisation_address.code;
            this.form.service_address.city = this.form.organisation_address.city;
            this.form.service_address.country_id = this.form.organisation_address.country_id;
        }

        /**
         * Copy organisation information to the service.
         */
        function copyToOrganisation() {
            this.form.organisation.name = this.form.service.name;
            this.form.organisation.telephone = this.form.service.telephone;
            this.form.organisation.mobile = this.form.service.mobile;
            this.form.organisation.email = this.form.service.email;
            this.form.organisation.website = this.form.service.website;
            this.form.organisation_address.street = this.form.service_address.street;
            this.form.organisation_address.address_line_1 = this.form.service_address.address_line_1;
            this.form.organisation_address.address_line_2 = this.form.service_address.address_line_2;
            this.form.organisation_address.address_line_3 = this.form.service_address.address_line_3;
            this.form.organisation_address.code = this.form.service_address.code;
            this.form.organisation_address.city = this.form.service_address.city;
            this.form.organisation_address.country_id = this.form.service_address.country_id;
        }

        /**
         * Copy the organisation information to the tax section.
         */
        function copyToTax() {
            this.form.tax.company_name = this.form.organisation.registered_legal_name;
            this.form.tax_address.street = this.form.organisation_address.street;
            this.form.tax_address.address_line_1 = this.form.organisation_address.address_line_1;
            this.form.tax_address.address_line_2 = this.form.organisation_address.address_line_2;
            this.form.tax_address.address_line_3 = this.form.organisation_address.address_line_3;
            this.form.tax_address.code = this.form.organisation_address.code;
            this.form.tax_address.city = this.form.organisation_address.city;
            this.form.tax_address.country_id = this.form.organisation_address.country_id;
        }

        /**
         * Copy the service information to the tax section.
         */
        function copyServiceToTax() {
            this.form.tax.company_name = this.form.service.name;
            this.form.tax_address.street = this.form.service_address.street;
            this.form.tax_address.address_line_1 = this.form.service_address.address_line_1;
            this.form.tax_address.address_line_2 = this.form.service_address.address_line_2;
            this.form.tax_address.address_line_3 = this.form.service_address.address_line_3;
            this.form.tax_address.code = this.form.service_address.code;
            this.form.tax_address.city = this.form.service_address.city;
            this.form.tax_address.country_id = this.form.service_address.country_id;
        }

        /**
         * Submit the form and perform validation on the request.
         */
        function submit() {
            if (this.updating === true) {

                // Overwrite the region ids, solves an issue with inserting into db.

                this.form.post(route('frontend.organisations.' + this.organisation.type + 's.organisation.update', [organisation.id]), {
                    onError: (page) => {
                        this.showErrors = true
                    },
                    onSuccess: (page) => {
                    }
                });

            } else {
                this.form.post(route('frontend.organisations.store'), {
                    onError: (page) => {
                        this.showErrors = true
                    }
                })
            }
        }


        return {
            user,
            userCheck,
            form,
            organisation_types,
            countries,
            service_types,
            currencies,
            copyToService,
            copyToOrganisation,
            copyToTax,
            copyServiceToTax,
            submit,
            organisation,
            updating,
            showErrors,
            openModal
        }

    },
    methods: {
        archive() {
            if (this.userCheck) {
                this.$swal.fire({
                    title: 'Archive ' + this.organisation.type.charAt(0).toUpperCase() + this.organisation.type.slice(1),
                    html: "You are archiving a " + this.organisation.type + ". <br/>Are you sure you'd like to do this?",
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No'
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.$inertia.delete(route('frontend.organisations.organisation.archive', this.organisation.id), {
                            preserveScroll: true
                        });
                    }
                });
            }
        },
        unarchive() {
            if (this.userCheck) {
                this.$swal.fire({
                    title: 'Unarchive ' + this.organisation.type.charAt(0).toUpperCase() + this.organisation.type.slice(1),
                    html: "You are unarchiving a " + this.organisation.type + ". <br/>Are you sure you'd like to do this?",
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No'
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.$inertia.get(route('frontend.organisations.organisation.unarchive', this.organisation.id), {
                            preserveScroll: true
                        });
                    }
                });
            }
        }
    }
}
</script>
